import {
  ConfidenceLabel,
  CopyPdfLinkButton,
  ManualJobMatchButton,
  ReviewHeaderLayout,
  ReviewHeaderStatus,
  StatusBadge,
  StFilingTargetLink,
} from "@/accounts-payable/common";
import type { ApReceipt } from "@/accounts-payable/types";
import {
  canManuallyMatchJob,
  formatApDate,
  invoiceStatusMeta,
  receiptPaymentLabel,
  stFilingTarget,
} from "@/accounts-payable/utils";
import { formatCentsOrDashIfZero } from "@/core/utils/money";

/**
 * The receipt review page's title block: the store, then the receipt's
 * number, date, payment and total; the job match with its job (or register
 * counterpart of `InvoiceReviewHeader`.
 */
export const ReceiptReviewHeader = ({
  receipt,
  businessId,
}: {
  receipt: ApReceipt;
  businessId: string | null;
}) => (
  <ReviewHeaderLayout
    title={receipt.vendor_name}
    facts={[
      receipt.receipt_number ?? "No receipt number",
      formatApDate(receipt.receipt_date),
      receiptPaymentLabel(receipt),
      <span key="total" className="font-semibold text-ink tabular-nums">
        {formatCentsOrDashIfZero(receipt.total_amount_cents)}
      </span>,
    ]}
    statuses={[
      <ReviewHeaderStatus key="job">
        <StatusBadge meta={invoiceStatusMeta(receipt)} />
        {stFilingTarget(receipt) ? (
          <StFilingTargetLink
            document={receipt}
            documentType="receipt"
            className="ml-0"
          />
        ) : (
          receipt.po_number && <span>PO / job: {receipt.po_number}</span>
        )}
        {stFilingTarget(receipt) && receipt.job_match_confidence != null && (
          <ConfidenceLabel value={receipt.job_match_confidence} />
        )}
      </ReviewHeaderStatus>,
    ]}
    actions={
      <>
        {canManuallyMatchJob(receipt) && (
          <ManualJobMatchButton
            businessId={businessId}
            documentId={receipt.id}
            documentType="receipt"
            agentRunId={receipt.agent_run_id}
            label={
              stFilingTarget(receipt) ? "Change job or project" : undefined
            }
          />
        )}
        <CopyPdfLinkButton document={receipt} />
      </>
    }
  />
);
